import React, { useState, useEffect, useCallback, ReactElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { actions as projectActions } from '../../actions/projectActions';
import { actions as userActions } from '../../actions/userActions';
import { actions as projectMgrDashboardActions } from '../../actions/projectMgrDashboardActions';
import { useDispatch, useSelector } from 'react-redux';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import type { Dispatch } from 'redux';
import { GlobalStyle, PageWrapper, ProjectOwnerName, ProjectName, ProjectHeader, BreadCrumb, DashboardsTab, StackedBarChartDiv } from './styles';
import Tile from './components/Tile/Tile';
import Routes from '@Constants/routes';
import StackedBarChart from '@Components/Common/StackedBarChart/StackedBarChart';
import ProjectDetails from './components/ProjectDetails/ProjectDetails';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import ProjectFiles from './components/Files/ProjectFiles';
import ProjectWaivers from './components/Waivers/ProjectWaivers';
import ProjectSupportTickets from './components/Support/ProjectSupportTickets';
import { isValidUrl } from '../../helpers/validation';
import { DEFAULT_PAGE_SIZE, SortColumnsType } from '@Constants/constants';
import Locations from './components/Locations/Locations';
import ProjectTasks from './components/ProjectTasks/ProjectTasks';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import InputText from '@Common/InputText';
import Toggle from '@Common/Toggle/Toggle';
import { useSafeAsyncEffect } from '@Components/Common/AbortableApi';
import { Project, TileValue, TaskData, ProjectOption, ProjectResource, ProjectEpicLabel, ProjectTag, ProjectDetailsState } from './PMDashboard.types';

const PMDashboard = (): ReactElement => {
    const dispatch: Dispatch = useDispatch();
    const { currentSubscriptionId: subscriptionId, windowWidth, customerRole } = useSelector((state: any) => state.user);
    const currentUserId = useSelector((state: any) => state.user.id);

    const [projectData, setProjectData] = useState<Project>({
        id: -1,
        name: '',
        projectManager: { name: '' },
        abbreviation: '',
        description: '',
        projectManagerId: 0,
        isActive: true,
        projectKey: '',
        supportAgentId: 0,
        subscriptionId: 0,
    });
    const [coachTasks, setCoachTasks] = useState<TileValue>();
    const [certifiedTasks, setCertifiedTasks] = useState<TileValue>();

    const [loadingTaskAndMilestones, setLoadingTaskAndMilestones] = useState(false);
    const [tasksPerWeek, setTasksPerWeek] = useState<Array<TaskData>>([]);

    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<number>(1);
    const [searchTerm, setSearchTerm] = useState('');
    const { projectKey: initialProjectKey } = useParams<{ projectKey: string }>();
    const [projectKey, setProjectKey] = useState<string>(initialProjectKey);
    const [graphWidth, setGraphWidth] = useState<number>(0);

    const newProject = {
        id: 0,
        name: '',
        description: '',
        abbreviation: '',
        canRequestProjectWaiver: false,
        isActive: true,
        subscriptionId: subscriptionId,
        projectManagerId: '',
        projectManagerName: '',
        supportAgentId: 0,
        supportAgentName: '',
        resources: [
            {
                id: 0,
                name: '',
                url: '',
                isActive: true,
                ordinal: 0,
            },
        ],
        projectEpicLabels: [
            {
                id: 0,
                name: '',
                isActive: true,
                ordinal: 0,
            },
        ],
        dateInactive: null,
        createdBy: '',
        createdDate: null,
        projectTagIds: [],
        zeroDateName: '',
    };

    const [projectDetailsState, setProjectDetailsState] = useState<ProjectDetailsState>({
        currentUserId: currentUserId,
        projects: [],
        projectManagers: [],
        supportAgents: [],
        projectTags: [],
        totalCount: 0,
        isProjectWaiversActive: false,
        isProjectReferenceDatesActive: false,
        isFetching: false,
        isSaving: false,
        selectedProject: newProject,
        editProject: newProject,
        newProject: newProject,
        drawerOpen: false,
        isCopyProjectDrawerOpen: false,
        isFormValid: true,
        searchParams: {
            subscriptionId: subscriptionId,
            isActive: true,
            projectName: '',
            abbreviation: '',
            description: '',
            projectManagerIds: [],
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,
            sortBy: 'name',
            sortDir: 'asc',
        },
        isAddNewTagModalLoading: false,
        newProjectTag: {
            name: '',
            color: '#509823',
            bgColor: '#5F0DF0',
        },
        isNewTagFormValid: true,
        dashboardLocationsCols: [],
        dashboardTasksCols: [],
        projectOptionsDropdownList: [],
        userSelectedFavoriteProjects: [],
    });

    const [selectedUserProjects, setSelectedUserProjects] = useState<ProjectOption[]>([]);
    const [pendingFavoritesSave, setPendingFavoritesSave] = useState(false);

    const loadProject = async (projectKey: string, signal: AbortSignal): Promise<any> =>
        dispatch(projectActions.getProject(projectKey, signal) as any);
    const loadCoachTasks = async (projectKey: string, signal: AbortSignal): Promise<any> =>
        dispatch(projectMgrDashboardActions.loadProjectCoachTasksGraph(projectKey, signal) as any);
    const loadCertifiedTasks = async (projectKey: string): Promise<any> =>
        dispatch(projectMgrDashboardActions.loadProjectCertifiedTasksGraph(projectKey) as any);
    const loadProjectListFilters = async (signal: AbortSignal): Promise<any> =>
        dispatch(projectActions.loadProjectListFilters(subscriptionId, signal) as any);

    const loadTasksAndMilestonesCompletionsGraph = async (projectKey: string, signal: AbortSignal): Promise<any> =>
        dispatch(projectMgrDashboardActions.tasksAndMilestonesCompletionsGraph(projectKey, signal) as any);

    const saveProjectAsync = async (project: any): Promise<any> => dispatch(projectActions.saveProject(project) as any);
    const saveLocationsColumns = async (params: any): Promise<any> => dispatch(userActions.saveUsersColumns(params) as any);
    const saveTasksColumns = async (params: any): Promise<any> => dispatch(userActions.saveUsersColumns(params) as any);
    const saveUserFavoriteProjectAsync = async (favoriteProjects: ProjectOption[]): Promise<any> =>
        dispatch(projectActions.saveUserFavoriteProject(favoriteProjects, projectDetailsState.userSelectedFavoriteProjects) as any);

    const loadFormFilters = async (signal: AbortSignal) => {
        try {
            setProjectDetailsState((prevState) => ({
                ...prevState,
                isFetching: true,
            }));

            const result = await loadProjectListFilters(signal);
            const payload = result.payload;
            if (result.type === types.LOAD_PROJECT_FILTERS_SUCCESS) {
                const addNewTagOption: ProjectTag = {
                    id: -9999,
                    name: '+ Add New Project Tag',
                    styles: {
                        backgroundColor: '#F7F5FF',
                        color: '#46389E',
                    },
                };

                const updatedProjectOptionsDropdownList = mapProjectOptionsDropdownList(payload.projectOptionsDropdownList);

                setProjectDetailsState((prevState) => ({
                    ...prevState,
                    projectManagers: payload.projectManagers,
                    supportAgents: payload.supportAgents,
                    totalCount: payload.totalCount,
                    isProjectWaiversActive: payload.isProjectWaiversActive,
                    isProjectReferenceDatesActive: payload.isProjectReferenceDatesActive,
                    projectTags: [...payload.tags, addNewTagOption],
                    dashboardLocationsCols: payload.dashboardLocationsCols,
                    dashboardTasksCols: payload.dashboardTasksCols,
                    projectOptionsDropdownList: updatedProjectOptionsDropdownList,
                    userSelectedFavoriteProjects: [...payload.userFavoriteProjects],
                }));
            } else if (result.type === types.LOAD_PROJECT_FILTERS_FAILURE) {
                Notification('error', 'An Error Occurred', payload.message);
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
        } finally {
            setProjectDetailsState((prevState) => ({
                ...prevState,
                isFetching: false,
            }));
        }
    };

    const save = async (): Promise<boolean> => {
        const project = projectDetailsState.editProject;
        // remove all blank resources
        if (project.resources) {
            project.resources = project.resources.filter((x: ProjectResource) => x?.name?.trim().length > 0 && x?.url?.trim().length > 0);
        }

        if (project.projectEpicLabels) {
            project.projectEpicLabels = project.projectEpicLabels.filter((x: ProjectEpicLabel) => x?.name?.trim().length > 0);
        }
        const result = await saveProjectAsync(project);

        if (result.type === types.CREATE_PROJECT_SUCCESS || result.type === types.UPDATE_PROJECT_SUCCESS) {
            Notification('success', 'Save Successful', 'Project saved successfully');
            return true;
        } else if (result.type === types.UPDATE_PROJECT_FAILURE || result.type === types.CREATE_PROJECT_FAILURE) {
            setProjectDetailsState((prevState) => ({
                ...prevState,
                errorMessage: result.payload.message,
            }));
            Notification('error', 'An Error Occurred', result.payload.message);
            return false;
        }
        return false;
    };

    const history = useHistory();

    const clickBack = () => {
        history.push(Routes.AdminHome + '/projects');
    };

    const mapProjectOptionsDropdownList = (list: any[]): ProjectOption[] => {
        if (!Array.isArray(list)) return [];

        return list.map((project) => ({
            id: project.option?.id ?? null,
            name: project.option?.name ?? '',
            projectKey: project.projectKey ?? '',
        }));
    };

    const formatProjectData = async (projectKey: string, signal?: AbortSignal) => {
        try {
            // Capture subscriptionId at the start to prevent race conditions
            const currentSubscriptionId = subscriptionId;
            const result = await loadProject(projectKey, signal!);
            const payload = result.payload;

            if (result.type === types.GET_PROJECT_SUCCESS) {
                /**
                 * SECURITY CHECK: Cross-Subscription Access Prevention (Issue #5722)
                 *
                 * This validation prevents users from accessing projects from different subscriptions
                 * by manipulating URLs. This is a defense-in-depth measure that works even if
                 * backend validation is bypassed.
                 *
                 * Validates:
                 * - User has a valid subscription ID (not null, undefined, or 0)
                 * - Project belongs to the user's current subscription
                 * - Uses strict comparison to prevent type coercion issues
                 */
                if (
                    currentSubscriptionId === undefined ||
                    currentSubscriptionId === null ||
                    currentSubscriptionId === 0 ||
                    !payload.project ||
                    payload.project.subscriptionId !== currentSubscriptionId
                ) {
                    // Log security violation for monitoring
                    console.warn('Security: Unauthorized project access attempt', {
                        projectKey,
                        userSubscriptionId: currentSubscriptionId,
                        projectSubscriptionId: payload.project?.subscriptionId,
                        timestamp: new Date().toISOString(),
                    });

                    Notification('error', 'Access Denied', 'You do not have permission to access this project.');
                    history.push(Routes.AdminHome + '/projects');
                    return;
                }

                setProjectData(payload.project);
                setProjectDetailsState((prevState) => ({
                    ...prevState,
                    editProject: {
                        ...payload.project,
                        isProjectWaiversActive: prevState.isProjectWaiversActive ?? true,
                        isProjectReferenceDatesActive: prevState.isProjectReferenceDatesActive ?? true,
                    },
                }));
            } else if (result.type === types.GET_PROJECT_FAILURE) {
                Notification('error', 'An Error Occurred', payload.message);
                history.push(Routes.AdminHome + '/projects');
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
            history.push(Routes.AdminHome + '/projects');
        }
    };

    const formatProjectCoachtasks = async (projectKey: string, signal: AbortSignal) => {
        try {
            const response = await loadCoachTasks(projectKey, signal);
            if (response.type === types.LOAD_PROJECT_COACH_TASKS_GRAPH_SUCCESS) {
                const totalTasks = calculateTotalTasks(response.payload.coachTasks.data);
                const completedTasks = calculateCompletedTasks(response.payload.coachTasks.data);

                setCoachTasks({ total: totalTasks, subtotal: completedTasks });
            } else if (response.type === types.LOAD_PROJECT_COACH_TASKS_GRAPH_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const calculateTotalTasks = (data: Array<TaskData>): number => {
        return data.reduce((acc, item) => acc + item.value, 0);
    };

    const calculateCompletedTasks = (data: Array<TaskData>): number => {
        const completeItem = data.find((item) => item.id === 'Complete');
        const completeValue = completeItem ? completeItem.value : 0;
        return completeValue;
    };

    const formatCertifiedTasks = async (projectKey: string, signal: AbortSignal) => {
        try {
            const response = await loadCertifiedTasks(projectKey);
            if (response.type === types.LOAD_PROJECT_CERTIFIED_TASKS_GRAPH_SUCCESS) {
                const totalTasks = calculateTotalTasks(response.payload.certifiedTasks.data);
                const completedTasks = calculateCompletedTasks(response.payload.certifiedTasks.data);

                setCertifiedTasks({ total: totalTasks, subtotal: completedTasks });
            } else if (response.type === types.LOAD_PROJECT_CERTIFIED_TASKS_GRAPH_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const formatTasksAndMilestonesCompletionsGraph = async (signal: AbortSignal) => {
        setLoadingTaskAndMilestones(true);
        try {
            const response = await loadTasksAndMilestonesCompletionsGraph(projectKey, signal);

            if (response.type === types.LOAD_PROJECT_TASKS_AND_MILESTONES_COMPLETIONS_GRAPH_SUCCESS) {
                // Handle success
                if (response.payload.tasksPerWeek) {
                    setTasksPerWeek(response.payload.tasksPerWeek);
                }
            } else if (response.type === types.LOAD_PROJECT_TASKS_AND_MILESTONES_COMPLETIONS_GRAPH_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setLoadingTaskAndMilestones(false);
        }
    };

    const saveUserFavoriteProjectAsyncHandler = useCallback(
        async (userFavoriteProjects: ProjectOption[], showNotification = false) => {
            try {
                const response = await saveUserFavoriteProjectAsync(userFavoriteProjects);
                if (response.type === types.POST_SAVE_FAVORITE_PROJECT_SUCCESS) {
                    if (showNotification) {
                        Notification('success', 'Save Successful', 'Project favorites saved successfully');
                    }
                } else if (response.type === types.POST_SAVE_FAVORITE_PROJECT_FAILURE) {
                    Notification('error', 'Error', response.payload.message);
                }
            } catch (error) {
                Notification('error', 'Error', error);
            }
        },
        [saveUserFavoriteProjectAsync]
    );

    const handleTabClick = (tabId: number) => {
        setActiveTab(tabId);
    };

    const onFormChange = (name: string, value: any) => {
        setProjectDetailsState((prevState) => ({
            ...prevState,
            editProject: { ...prevState.editProject, [name]: value },
        }));
    };

    const saveClick = async (e: React.FormEvent) => {
        e.preventDefault();

        const { name, abbreviation, projectManagerId, resources } = projectDetailsState.editProject;

        const urlsValid = urlsIsValid(resources);
        const isFormValid = urlsValid && name.trim().length > 0 && abbreviation.trim().length > 0 && projectManagerId !== '';

        setProjectDetailsState((prevState) => ({
            ...prevState,
            editProject: {
                ...prevState.editProject,
                isFormValid,
            },
        }));

        if (isFormValid) {
            if (await save()) {
                await formatProjectData(projectKey);
            }
        }
    };

    const urlsIsValid = (resources: ProjectResource[]): boolean => {
        if (!resources || resources.length === 0) {
            return true;
        }

        return resources.every((r) => r.url === '' || isValidUrl(r.url));
    };

    const onToggleChange = (name: string, value: boolean) => {
        history.push(Routes.ProjectManagerDashboard + `/${projectKey}`);
    };

    const onLocationsColumnChange = async (newColumns: Array<string>, saveMongo: boolean) => {
        // Save columns personalization
        if (saveMongo) {
            await saveLocationsColumns({ columns: newColumns, columnsType: SortColumnsType.PmDashboardPageLocations });
        }

        setProjectDetailsState((prevState) => ({
            ...prevState,
            dashboardLocationsCols: newColumns,
        }));
    };

    const onTasksColumnChange = async (newColumns: Array<string>) => {
        // Save columns personalization
        await saveTasksColumns({ columns: newColumns, columnsType: SortColumnsType.PmDashboardPageTasks });

        setProjectDetailsState((prevState) => ({
            ...prevState,
            dashboardTasksCols: newColumns,
        }));
    };

    const onChangeProject = (e: any) => {
        const project = projectDetailsState.projectOptionsDropdownList?.find((p) => p.id === e.value.id);

        if (project) {
            const projectKey = project.projectKey.toLowerCase();
            setProjectKey(projectKey);
            history.push(`${Routes.Admin}/project-dashboard/${projectKey}`);
        } else {
            Notification('error', 'Project Not Found', `Project with ID ${e.value.id} not found.`);
        }
    };

    useSafeAsyncEffect(
        async (signal) => {
            setIsLoading(true);

            // Load form filters first to set isProjectWaiversActive and isProjectReferenceDatesActive
            await loadFormFilters(signal);

            // Then run other operations in parallel
            await Promise.all([
                formatProjectData(projectKey, signal),
                formatProjectCoachtasks(projectKey, signal),
                formatCertifiedTasks(projectKey, signal),
                formatTasksAndMilestonesCompletionsGraph(signal),
            ]);

            setIsLoading(false);
        },
        [projectKey]
    );

    const tabs = [
        {
            id: 1,
            label: 'Locations + Teams',
            component: (
                <Locations
                    projectData={projectData}
                    subscriptionId={subscriptionId}
                    userColumns={projectDetailsState.dashboardLocationsCols}
                    onColumnsChange={onLocationsColumnChange}
                />
            ),
            icon: 'fa-location-dot',
        },
        {
            id: 2,
            label: 'Task Lists',
            component: (
                <ProjectTasks
                    projectData={projectData}
                    subscriptionId={subscriptionId}
                    userColumns={projectDetailsState.dashboardTasksCols}
                    onColumnsChange={onTasksColumnChange}
                />
            ),
            icon: 'fa-square-list',
        },
        {
            id: 3,
            label: 'Project Files',
            component: <ProjectFiles subscriptionId={subscriptionId} projectKey={projectKey} windowWidth={windowWidth} />,
            icon: 'fa-files',
        },
        {
            id: 4,
            label: 'Waiver Requests',
            component: (
                <ProjectWaivers
                    subscriptionId={subscriptionId}
                    projectId={projectData.id}
                    projectKey={projectKey}
                    windowWidth={windowWidth}
                    customerRole={customerRole}
                />
            ),
            icon: 'fa-file-lines',
        },
        {
            id: 5,
            label: 'Project Support',
            component: <ProjectSupportTickets subscriptionId={subscriptionId} projectId={projectData.id} windowWidth={windowWidth} />,
            icon: 'fa-messages',
        },
        {
            id: 6,
            label: 'Project Details',
            component: <ProjectDetails projectDetails={projectDetailsState} onFormChange={onFormChange} onSubmit={saveClick}></ProjectDetails>,
            icon: 'fa-info-circle',
        },
    ];

    const itemTemplateFavorite = (option: ProjectOption) => {
        const isSelected = selectedUserProjects.some((i) => i.id === option.id);

        return (
            <span className='p-dropdown-item-label' style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Greycliff', fontWeight: '400', fontSize: '12px' }}>{option.name}</span>
                <span
                    className='ms-2'
                    style={{ cursor: 'pointer', fontSize: '18px', marginRight: '0px' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleFavoriteProject(option);
                    }}>
                    {isSelected ? (
                        <i className='fa-regular fa-star' style={{ color: 'gold' }}></i>
                    ) : (
                        <i className='fa-regular fa-star' style={{ color: 'gray' }}></i>
                    )}
                </span>
            </span>
        );
    };

    const toggleFavoriteProject = (item: ProjectOption) => {
        setSelectedUserProjects((prev) => {
            const updated = prev.some((i) => i.id === item.id) ? prev.filter((i) => i.id !== item.id) : [...prev, item];
            return updated;
        });

        // Trigger save after state update
        setPendingFavoritesSave(true);
    };

    const dropdownHeader = (
        <InputText
            type='text'
            name='searchFavorite'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder='Search...'
            className='w-full'
        />
    );

    const renderTitle = (): string => {
        if (projectData?.name) {
            return `Project Control Hub - ${projectData.name}`;
        }

        return 'Project Control Hub';
    };

    const handleResize = () => {
        const graphSection = document.getElementById('graph-section');
        if (graphSection) {
            const { width } = graphSection.getBoundingClientRect();
            setGraphWidth(width * 0.95); // Set graph width to 95% of the graph section width
        }
    };

    useEffect(() => {
        const title = renderTitle();
        document.title = title;
    }, [projectData?.name]);

    useEffect(() => {
        handleResize(); // Initial call to set width on component mount
        window.addEventListener('resize', handleResize);
    }, [tasksPerWeek, isLoading]);

    useEffect(() => {
        // Only initialize from server state when first loading
        if (!projectDetailsState.isFetching && projectDetailsState.userSelectedFavoriteProjects && selectedUserProjects.length === 0) {
            setSelectedUserProjects(projectDetailsState.userSelectedFavoriteProjects);
        }
    }, [projectDetailsState.userSelectedFavoriteProjects, projectDetailsState.isFetching, selectedUserProjects]);

    // Handle saving favorites when state changes and save is pending
    useEffect(() => {
        if (pendingFavoritesSave && selectedUserProjects.length > 0) {
            saveUserFavoriteProjectAsyncHandler(selectedUserProjects, true);
            setPendingFavoritesSave(false);
        }
    }, [selectedUserProjects, pendingFavoritesSave, saveUserFavoriteProjectAsyncHandler]);

    return (
        <LoadingIndicator isLoading={isLoading} hideWhileLoading={true} isFullscreen={true}>
            <PageWrapper>
                <GlobalStyle />
                <div className='row'>
                    <div className='col-10'>
                        <a onClick={() => clickBack()}>
                            <svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                <path
                                    d='M9.6126 13.5C9.50961 13.5005 9.40755 13.4804 9.31243 13.4409C9.21731 13.4014 9.13103 13.3433 9.05865 13.2701L3.83277 8.04416C3.68597 7.89718 3.60352 7.69795 3.60352 7.49022C3.60352 7.28249 3.68597 7.08325 3.83277 6.93628L9.05865 1.71039C9.20725 1.57192 9.40379 1.49654 9.60687 1.50012C9.80995 1.50371 10.0037 1.58597 10.1473 1.72959C10.291 1.87322 10.3732 2.06698 10.3768 2.27005C10.3804 2.47313 10.305 2.66968 10.1665 2.81827L5.4946 7.49022L10.1665 12.1622C10.3133 12.3091 10.3958 12.5084 10.3958 12.7161C10.3958 12.9238 10.3133 13.1231 10.1665 13.2701C10.0942 13.3433 10.0079 13.4014 9.91277 13.4409C9.81764 13.4804 9.71559 13.5005 9.6126 13.5Z'
                                    fill='#6C757D'
                                />
                            </svg>
                            <BreadCrumb>Projects</BreadCrumb>
                        </a>
                    </div>
                    <div className='col-2 d-flex justify-content-end'>
                        <Toggle name='isActive' text='New Experience' checked={true} onChange={onToggleChange} />
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <ProjectHeader>
                            <div className='left-group multiselect-favorites'>
                                {!isLoading && !projectDetailsState.isFetching && (
                                    <Dropdown
                                        value={selectedUserProjects[0] || {}}
                                        options={projectDetailsState.projectOptionsDropdownList}
                                        onChange={onChangeProject}
                                        optionLabel='name'
                                        placeholder=''
                                        className='w-full flex justify-between items-center w-full'
                                        showClear
                                        filter
                                        filterBy='name'
                                        style={{ border: 'none', justifyContent: 'space-between' }}
                                        itemTemplate={itemTemplateFavorite}
                                    />
                                )}
                                <ProjectName>{projectData?.name}</ProjectName>
                            </div>
                            <ProjectOwnerName>
                                <span>{projectData?.projectManager.name}</span>
                                <span>Project Owner</span>
                            </ProjectOwnerName>
                        </ProjectHeader>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-6 col-lg-3 col-xl-2'>
                        <Tile
                            title='Certified Complete'
                            subtitle='Teams who have signed off on project completion'
                            total={certifiedTasks?.total}
                            subtotal={certifiedTasks?.subtotal}
                        />
                    </div>
                    <div id='graph-section' className='col-md-12 col-lg-6 col-xl-8'>
                        <Tile title='Task and Milestone Completion' subtitle='A summary of all projects tasks within published locations'>
                            {loadingTaskAndMilestones ? (
                                <Skeleton width='100%' height='100%' borderRadius='16px' />
                            ) : (
                                <StackedBarChartDiv>
                                    <StackedBarChart data={tasksPerWeek} width={graphWidth} title='' />
                                </StackedBarChartDiv>
                            )}
                        </Tile>
                    </div>
                    <div className='col-md-6 col-lg-3 col-xl-2'>
                        <Tile
                            title='Coach Assigned'
                            subtitle='Team-specific individual responsible for leading the project'
                            total={coachTasks?.total}
                            subtotal={coachTasks?.subtotal}
                        />
                    </div>
                </div>
                <div className='row g-0'>
                    <DashboardsTab className='nav nav-tabs mt-5 w-100'>
                        {tabs.map((tab) => (
                            <li key={tab.id} className='nav-item'>
                                <a
                                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => handleTabClick(tab.id)}
                                    type='button'>
                                    <i className={`fa-regular ${tab.icon}`} />
                                    <span className='tab-label'>{tab.label}</span>
                                </a>
                            </li>
                        ))}
                    </DashboardsTab>
                    <div className='col-12'>{tabs.find((tab) => tab.id === activeTab)?.component}</div>
                </div>
            </PageWrapper>
        </LoadingIndicator>
    );
};

export default PMDashboard;
